import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosSetup';
import PushNotifications from './pushNotifications';
import cfg from '../config';
import {
  holeAngebot,
  beiAngebot,
  installierenAnstossen,
  istInstalliert,
  istIOS,
  istHandy,
  istSamsungInternet,
} from '../installPrompt';

const SCHRITT_WILLKOMMEN = {
  icon: '👋',
  title: 'Willkommen!',
  desc: 'Schön, dass du dabei bist! In wenigen Schritten richtest du die App ein.',
};

const SCHRITT_INSTALLATION = {
  icon: '📲',
  title: 'App auf dein Handy legen',
  desc: 'Als installierte App startet sie schneller, läuft ohne Browserleiste und kann dich benachrichtigen.',
  component: 'install',
};

const SCHRITT_PUSH = {
  icon: '🔔',
  title: 'Benachrichtigungen',
  desc: 'Aktiviere Push-Benachrichtigungen, damit du nichts Wichtiges verpasst.',
  component: 'push',
};

// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const SCHRITT_PROFIL = () => ({
  icon: '👤',
  title: 'Dein Profil',
  desc: `Halte deine Kontaktdaten aktuell – das erleichtert ${cfg.adminRoleName} die Arbeit.`,
  component: 'profile',
});

const SCHRITT_FERTIG = {
  icon: '🎉',
  title: 'Alles bereit!',
  desc: 'Du bist startklar. Viel Spaß mit der App!',
};

// Der Installationsschritt erscheint nur auf dem Handy und nur, solange die
// App dort noch nicht installiert ist. Am Rechner waere er sinnlos, in der
// bereits installierten App ebenso.
//
// Er steht bewusst vor den Benachrichtigungen: Auf dem iPhone liefert iOS
// ueberhaupt erst dann Push aus, wenn die App auf dem Home-Bildschirm liegt.
// Andersherum liefe der Push-Schritt fuer iPhone-Nutzer ins Leere.
function baueSchritte() {
  const schritte = [SCHRITT_WILLKOMMEN];
  if (istHandy() && !istInstalliert()) schritte.push(SCHRITT_INSTALLATION);
  schritte.push(SCHRITT_PUSH, SCHRITT_PROFIL(), SCHRITT_FERTIG);
  return schritte;
}

const S = {
  page:    { minHeight: '100vh', background: '#0f1117', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' },
  card:    { background: '#1e2130', border: '1px solid #2e3347', borderRadius: 14, width: '100%', maxWidth: 480, padding: '2rem 1.75rem' },
  dots:    { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: '2rem' },
  icon:    { fontSize: '2.5rem', textAlign: 'center', marginBottom: '1rem' },
  title:   { color: '#e8eaf0', fontWeight: 700, fontSize: '1.25rem', textAlign: 'center', marginBottom: '0.5rem' },
  desc:    { color: '#7b82a0', fontSize: '0.92rem', textAlign: 'center', marginBottom: '1.5rem', lineHeight: 1.6 },
  footer:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.75rem', gap: 12 },
  btnPrimary: {
    background: '#ffc107', color: '#000', border: 'none', borderRadius: 8,
    padding: '9px 24px', fontWeight: 600, cursor: 'pointer', flex: 1,
  },
  btnSecondary: {
    background: 'none', color: '#7b82a0', border: '1px solid #2e3347', borderRadius: 8,
    padding: '9px 20px', cursor: 'pointer',
  },
  profileBtn: {
    display: 'block', width: '100%', textAlign: 'center',
    background: 'none', border: '1px solid #3a3f55', color: '#60a5fa',
    borderRadius: 8, padding: '9px 20px', cursor: 'pointer', marginTop: 8, textDecoration: 'none',
  },
  installBtn: {
    display: 'block', width: '100%', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 8, padding: '11px 20px', fontWeight: 600,
    cursor: 'pointer',
  },
  liste:   { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  zeile:   { display: 'flex', alignItems: 'flex-start', gap: 12, color: '#c9cee0', fontSize: '0.9rem', lineHeight: 1.5 },
  nummer:  {
    flexShrink: 0, width: 24, height: 24, borderRadius: 12, background: '#2e3347',
    color: '#ffc107', fontSize: '0.8rem', fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  hinweis: { color: '#7b82a0', fontSize: '0.82rem', lineHeight: 1.5, marginTop: 14, marginBottom: 0 },
  warnung: {
    color: '#ffcb57', fontSize: '0.88rem', lineHeight: 1.6, margin: 0,
    background: 'rgba(240,165,0,0.1)', border: '1px solid rgba(240,165,0,0.3)',
    borderRadius: 8, padding: '12px 14px',
  },
  erfolg:  {
    color: '#4ade80', fontSize: '0.92rem', lineHeight: 1.6, textAlign: 'center',
    background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)',
    borderRadius: 8, padding: '12px 14px', margin: 0,
  },
};

const InstallSchritt = () => {
  const [angebot, setAngebot]         = useState(holeAngebot);
  const [installiert, setInstalliert] = useState(false);
  const [laeuft, setLaeuft]           = useState(false);

  // Das Angebot kann vor oder nach dem Einhaengen eintreffen, deshalb beides:
  // der Anfangswert oben holt ein bereits vorliegendes, dieser Zuhoerer faengt
  // ein spaeter eintreffendes ab.
  useEffect(() => beiAngebot(setAngebot), []);

  useEffect(() => {
    const fertig = () => setInstalliert(true);
    window.addEventListener('appinstalled', fertig);
    return () => window.removeEventListener('appinstalled', fertig);
  }, []);

  const installieren = async () => {
    setLaeuft(true);
    try {
      await installierenAnstossen();
    } finally {
      setLaeuft(false);
    }
  };

  if (installiert) {
    return (
      <p style={S.erfolg}>
        ✓ Die App liegt jetzt auf deinem Startbildschirm. Öffne sie ab sofort
        über das neue Symbol.
      </p>
    );
  }

  // ---------- iPhone und iPad ----------
  // Apple bietet keinen Installationsknopf an. Der Weg ueber das Teilen-Menue
  // ist die einzige Moeglichkeit und existiert ausserdem nur in Safari.
  if (istIOS()) {
    return (
      <div>
        <ol style={S.liste}>
          <li style={S.zeile}>
            <span style={S.nummer}>1</span>
            <span>Unten in der Leiste auf <strong>Teilen</strong> tippen – das Quadrat mit dem Pfeil nach oben.</span>
          </li>
          <li style={S.zeile}>
            <span style={S.nummer}>2</span>
            <span>In der Liste nach unten wischen und <strong>„Zum Home-Bildschirm"</strong> wählen.</span>
          </li>
          <li style={S.zeile}>
            <span style={S.nummer}>3</span>
            <span>Oben rechts mit <strong>Hinzufügen</strong> bestätigen.</span>
          </li>
        </ol>
        <p style={S.hinweis}>
          Das klappt nur in <strong>Safari</strong> – in Chrome oder Firefox auf
          dem iPhone fehlt der Eintrag. Öffne die App danach über das neue
          Symbol und melde dich dort einmal an. Erst dann kann dich dein iPhone
          benachrichtigen.
        </p>
      </div>
    );
  }

  // ---------- Samsung Internet ----------
  // Dieser Browser verpackt die App selbst und zielt dabei auf eine veraltete
  // Android-Version. Android 14 lehnt das ab, Play Protect meldet "Unsichere
  // App blockiert". Der Weg ueber Chrome umgeht das, weil dort ein
  // Google-Dienst das Paket mit aktueller Zielversion baut.
  if (istSamsungInternet()) {
    return (
      <div>
        <p style={S.warnung}>
          In <strong>Samsung Internet</strong> bricht die Installation mit der
          Meldung „Unsichere App blockiert" ab. Das liegt am Browser, nicht an
          dieser App – er verpackt sie auf eine Art, die neuere Android-Versionen
          zurückweisen.
        </p>
        <ol style={{ ...S.liste, marginTop: 14 }}>
          <li style={S.zeile}>
            <span style={S.nummer}>1</span>
            <span>Diese Seite in <strong>Chrome</strong> öffnen – die Adresse lautet <strong>{window.location.host}</strong>.</span>
          </li>
          <li style={S.zeile}>
            <span style={S.nummer}>2</span>
            <span>Dort anmelden und im Menü <strong>⋮</strong> auf <strong>„App installieren"</strong> tippen.</span>
          </li>
        </ol>
        <p style={S.hinweis}>
          Du kannst diesen Schritt auch überspringen und die App weiter im
          Browser nutzen – es geht dabei nichts verloren.
        </p>
      </div>
    );
  }

  // ---------- Android ----------
  if (angebot) {
    return (
      <div>
        <button style={S.installBtn} onClick={installieren} disabled={laeuft}>
          {laeuft ? 'Einen Moment…' : 'Jetzt installieren'}
        </button>
        <p style={S.hinweis}>
          Dein Handy fragt gleich nach einer Bestätigung. Danach findest du die
          App wie jede andere auf deinem Startbildschirm.
        </p>
      </div>
    );
  }

  // Kein Angebot: entweder hat der Browser es noch nicht geschickt, oder er
  // unterstuetzt die Installation nicht. Dann bleibt der Weg ueber das Menue.
  return (
    <div>
      <ol style={S.liste}>
        <li style={S.zeile}>
          <span style={S.nummer}>1</span>
          <span>Oben rechts im Browser auf das Menü <strong>⋮</strong> tippen.</span>
        </li>
        <li style={S.zeile}>
          <span style={S.nummer}>2</span>
          <span><strong>„App installieren"</strong> wählen – je nach Browser heißt es auch <strong>„Zum Startbildschirm hinzufügen"</strong>.</span>
        </li>
      </ol>
      <p style={S.hinweis}>
        Fehlt der Eintrag, ist die App auf diesem Gerät bereits installiert oder
        der Browser unterstützt es nicht. Dann kannst du diesen Schritt
        überspringen.
      </p>
    </div>
  );
};

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  // Einmalig beim Einhaengen bestimmt. Wuerde sich die Liste waehrend des
  // Durchlaufs aendern - etwa weil die Installation gerade geglueckt ist -,
  // zeigte der Schrittzaehler ploetzlich auf den falschen Eintrag.
  const [steps] = useState(baueSchritte);

  useEffect(() => {
    if (localStorage.getItem('onboardingCompleted') === '1') {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post('/api/complete-onboarding');
    } catch {}
    localStorage.setItem('onboardingCompleted', '1');
    navigate('/', { replace: true });
  };

  const isLast = step === steps.length - 1;
  const current = steps[step];

  return (
    <div style={S.page}>
      <div style={S.card}>
        {/* Fortschritts-Punkte */}
        <div style={S.dots}>
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === step ? 24 : 8, height: 8, borderRadius: 4,
                background: i <= step ? '#ffc107' : '#2e3347',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>

        {/* Schritt-Inhalt */}
        <div style={S.icon}>{current.icon}</div>
        <p style={S.title}>{current.title}</p>
        <p style={S.desc}>{current.desc}</p>

        {current.component === 'install' && (
          <div style={{ marginBottom: 8 }}>
            <InstallSchritt />
          </div>
        )}

        {current.component === 'push' && (
          <div style={{ marginBottom: 8 }}>
            <PushNotifications />
          </div>
        )}

        {current.component === 'profile' && (
          <a href="/meine-daten" style={S.profileBtn}>
            Profil aufrufen →
          </a>
        )}

        {/* Navigation */}
        <div style={S.footer}>
          {step > 0 ? (
            <button style={S.btnSecondary} onClick={() => setStep(s => s - 1)}>
              Zurück
            </button>
          ) : (
            <button style={S.btnSecondary} onClick={complete} disabled={completing}>
              Überspringen
            </button>
          )}

          {isLast ? (
            <button style={S.btnPrimary} onClick={complete} disabled={completing}>
              {completing ? 'Wird gespeichert…' : 'Los geht\'s!'}
            </button>
          ) : (
            // Begrenzt: Mehrere Klicks im selben Rendervorgang haben den
            // Zaehler sonst ueber das Ende der Liste geschoben, worauf die
            // Ansicht mit "Etwas ist schiefgelaufen" abbrach.
            <button style={S.btnPrimary} onClick={() => setStep(s => Math.min(s + 1, steps.length - 1))}>
              Weiter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
